<template>
  <div id="feeds-page">
    <div class="d-flex align-center justify-space-between mb-4">
      <h2 style="font-size: 20px" class="my-0">Facebook Catalog Management</h2>
      <v-btn color="primary" depressed @click="onAdd"> <v-icon left>mdi-plus</v-icon> ADD CATALOG </v-btn>
    </div>
    <div class="filter-data-area mb-4">
      <v-text-field
        v-model.trim="model.searchText"
        outlined
        dense
        placeholder="Catalog Name"
        class="mr-2"
        style="max-width: 300px"
        hide-details
        @keyup.enter="getFeeds"
      />
      <v-btn color="primary" depressed class="mr-2" @click="getFeeds">
        <v-icon left small>mdi-magnify</v-icon>
        Search
      </v-btn>
      <v-btn outlined depressed @click="clearFilters">
        <v-icon left small>mdi-close-circle</v-icon>
        Clear
      </v-btn>
      <v-btn outlined depressed @click="getFeeds" :loading="isLoading" class="ml-auto">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <v-data-table
      :loading="isLoading"
      :headers="headers"
      :items="feedList"
      :items-per-page="pagination.limit"
      :page.sync="pagination.page"
      no-data-text="There are no feeds"
      item-key="_id"
      class="mb-4"
      hide-default-footer
    >
      <template v-slot:[`item.id`]="{ item }">
        <div class="d-flex align-center">
          <span style="color: var(--v-primary-base); font-weight: 500;" class="mr-2">
            {{ item._id }}
          </span>
          <v-btn icon x-small @click.stop="copyToClipboard(item._id)">
            <v-icon x-small>mdi-content-copy</v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:[`item.name`]="{ item }">
        {{ item.name }}
      </template>
      <template v-slot:[`item.products`]="{ item }">
        <span class="border-count" style="cursor: pointer;" @click.stop="onViewProducts(item)">
          {{ item.productCount || 0 }} {{ item.productCount === 1 ? 'Product' : 'Products' }}
        </span>
      </template>
      <template v-slot:[`item.fbBm`]="{ item }">
        <span v-if="item.fbBm"> {{ item.fbBm.business_name }} ({{ item.fbBm.business_id }}) </span>
        <span v-else>-</span>
      </template>
      <template v-slot:[`item.createdAt`]="{ item }">
        <span>{{ formatDate(item.createdAt) }}</span>
      </template>
      <template v-slot:[`item.autoSync`]="{ item }">
        <v-switch
          v-model="item.autoSync"
          dense
          hide-details
          class="ma-0 pa-0 d-inline-flex"
          color="primary"
          :loading="!!isLoadingSyncMap[item._id]"
          @change="toggleAutoSync(item, $event)"
        />
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <v-chip
          small
          :color="getStatusColor(item.status)"
          text-color="white"
          class="font-weight-medium text-capitalize"
        >
          {{ formatStatusText(item.status) }}
        </v-chip>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-end">
          <v-btn small @click.stop="onEdit(item)" class="mr-2" depressed>
            <v-icon small left>mdi-pencil</v-icon>
            Edit
          </v-btn>

          <v-btn
            :loading="!!isLoadingPublishMap[item._id]"
            class="mr-2"
            depressed
            small
            color="primary"
            @click.stop="handlePublish(item)"
          >
            Publish
          </v-btn>

          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="onViewProducts(item)">
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-view-list</v-icon>
                </v-list-item-icon>
                <v-list-item-title>View Products</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="item.error" @click="onViewError(item)">
                <v-list-item-icon class="mr-2">
                  <v-icon small color="error">mdi-alert-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="error--text">View Error</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="onDelete(item)" class="error--text">
                <v-list-item-icon class="mr-2">
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
    </v-data-table>
    <nav
      v-if="feedList.length"
      class="d-flex flex-column flex-sm-row align-center align-sm-start justify-sm-space-between"
    >
      <v-pagination
        :limitAdd="10"
        :visible="7"
        :length="pagination.length"
        :total-visible="10"
        v-model="pagination.page"
        @input="getFeeds"
      ></v-pagination>
    </nav>

    <!-- Dialog for Adding Catalog -->
    <v-dialog v-model="showCatalogDialog" fullscreen persistent>
      <CatalogDialog v-if="showCatalogDialog" :catalog="selectedCatalog" @close="onCloseDialog" />
    </v-dialog>

    <!-- Dialog for Selecting BM -->
    <v-dialog v-model="showBmDialog" max-width="500" persistent>
      <BmDialog v-if="showBmDialog" :feed="currentPublishingFeed" @close="onCloseBmDialog" />
    </v-dialog>

    <!-- Products Dialog -->
    <ProductsDialog v-model="showProductsDialog" :catalog="selectedCatalog" @close="showProductsDialog = false" />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" width="500" persistent>
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this catalog? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" text @click="confirmDelete" :loading="deleteLoading">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Details Dialog -->
    <v-dialog v-model="showErrorDialog" width="600" scrollable>
      <v-card>
        <v-card-title class="error--text font-weight-bold">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Error Details
        </v-card-title>
        <v-card-text class="pt-4">
          <div
            style="white-space: pre-wrap; font-family: monospace; background: #f5f5f5; padding: 12px; border-radius: 4px; border: 1px solid #e0e0e0; max-height: 400px; overflow-y: auto;"
          >
            {{ typeof selectedError === 'object' ? JSON.stringify(selectedError, null, 2) : selectedError }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showErrorDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { feedService } from '@/apis/feed';
import moment from 'moment';
import BmDialog from './components/BmDialog.vue';
import CatalogDialog from './components/CatalogDialog.vue';
import ProductsDialog from './components/ProductsDialog.vue';

export default {
  components: {
    CatalogDialog,
    BmDialog,
    ProductsDialog,
  },
  data() {
    return {
      showCatalogDialog: false,
      showBmDialog: false,
      showProductsDialog: false,
      selectedCatalog: null,
      showDeleteDialog: false,
      deleteItem: null,
      deleteLoading: false,
      showErrorDialog: false,
      selectedError: null,
      currentPublishingFeed: null,
      feedList: [],
      model: {
        searchText: null,
      },
      isLoading: true,
      isLoadingPublishMap: {},
      isLoadingSyncMap: {},
      pagination: {
        page: 1,
        length: 1,
        limit: 10,
      },
      headers: [
        {
          text: 'Catalog ID',
          value: 'id',
          sortable: false,
        },
        {
          text: 'Catalog Name',
          value: 'name',
          sortable: false,
        },
        {
          text: 'Products',
          value: 'products',
          sortable: false,
          align: 'center',
        },
        {
          text: 'Business Manager',
          value: 'fbBm',
          sortable: false,
        },
        {
          text: 'Created At',
          value: 'createdAt',
          sortable: false,
        },
        {
          text: 'Auto Sync',
          value: 'autoSync',
          sortable: false,
          align: 'center',
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false,
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'right',
        },
      ],
    };
  },
  methods: {
    async getFeeds() {
      try {
        this.isLoading = true;
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          name: this.model.searchText,
        };
        const feeds = await feedService.getFeeds(params);
        if (feeds.status === 200) {
          this.feedList = feeds.data?.data || [];
          this.pagination.length = Math.ceil(feeds.data?.pagination?.total / this.pagination.limit);
        }
      } catch (error) {
        console.error('Error fetching feeds:', error);
      } finally {
        this.isLoading = false;
      }
    },
    onCloseDialog(success) {
      this.showCatalogDialog = false;
      this.selectedCatalog = null;
      if (success) {
        this.getFeeds();
      }
    },
    onAdd() {
      this.selectedCatalog = null;
      this.showCatalogDialog = true;
    },
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$store.commit('setMessages', {
            type: 'success',
            messages: 'Copied to clipboard',
          });
        })
        .catch(() => {
          this.$store.commit('setMessages', {
            type: 'error',
            messages: 'Failed to copy',
          });
        });
    },
    openBmSelectDialog(feed) {
      this.currentPublishingFeed = feed;
      this.showBmDialog = true;
    },
    onCloseBmDialog(success) {
      this.showBmDialog = false;
      this.currentPublishingFeed = null;
      if (success) {
        this.getFeeds();
      }
    },
    clearFilters() {
      this.model.searchText = '';
      this.pagination.page = 1;
      this.getFeeds();
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD/MM/YYYY HH:mm:ss');
    },
    async handlePublish(item) {
      if (!item.fbBmId || item.fbBmId.trim() === '') {
        this.currentPublishingFeed = item;
        this.showBmDialog = true;
      } else {
        try {
          this.$set(this.isLoadingPublishMap, item._id, true);
          const payload = {
            fb_catalog_id: item._id,
          };
          await feedService.publishFBCatalog(payload);
          this.$store.commit('setMessages', {
            type: 'success',
            messages: 'Catalog published successfully',
          });
          this.getFeeds();
        } catch (error) {
          console.error(error);
          this.$store.commit('setMessages', {
            type: 'error',
            messages: error?.response?.data?.message || 'Failed to publish catalog',
          });
        } finally {
          this.$set(this.isLoadingPublishMap, item._id, false);
        }
      }
    },
    async toggleAutoSync(item, value) {
      try {
        this.$set(this.isLoadingSyncMap, item._id, true);
        const payload = {
          autoSync: Boolean(value),
        };
        await feedService.autoSyncCatalog(item._id, payload);
        this.$store.commit('setMessages', {
          type: 'success',
          messages: `Auto sync ${value ? 'enabled' : 'disabled'} successfully`,
        });
      } catch (error) {
        console.error('Toggle auto sync error:', error);
        item.autoSync = !value;
        this.$store.commit('setMessages', {
          type: 'error',
          messages: error?.response?.data?.message || 'Failed to update auto sync status',
        });
      } finally {
        this.$set(this.isLoadingSyncMap, item._id, false);
      }
    },
    onEdit(item) {
      this.selectedCatalog = item;
      this.showCatalogDialog = true;
    },
    onViewProducts(item) {
      this.selectedCatalog = item;
      this.showProductsDialog = true;
    },
    onViewError(item) {
      this.selectedError = item.error;
      this.showErrorDialog = true;
    },
    onDelete(item) {
      this.deleteItem = item;
      this.showDeleteDialog = true;
    },
    async confirmDelete() {
      try {
        this.deleteLoading = true;
        await feedService.deleteCatalog(this.deleteItem._id);
        this.$store.commit('setMessages', {
          type: 'success',
          messages: 'Catalog deleted successfully',
        });
        this.showDeleteDialog = false;
        this.deleteItem = null;
        this.getFeeds();
      } catch (error) {
        console.error('Error deleting catalog:', error);
        this.$store.commit('setMessages', {
          type: 'error',
          messages: error?.response?.data?.message || 'Failed to delete catalog',
        });
      } finally {
        this.deleteLoading = false;
      }
    },
    getStatusColor(status) {
      switch (status) {
        case 'PUBLISHED':
          return 'success';
        case 'PUBLISHING':
        case 'IMPORTING':
          return 'info';
        case 'IMPORTED':
          return 'primary';
        case 'NOT_IMPORT':
        default:
          return 'grey';
      }
    },
    formatStatusText(status) {
      if (!status) return 'N/A';
      return status.toLowerCase().replace('_', ' ');
    },
  },
  created() {
    this.getFeeds();
  },
};
</script>

<style lang="scss" scoped>
.filter-data-area {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin-top: 10px;
  flex-wrap: wrap;
  gap: 8px;
}
#feeds-page {
  .no-data {
    text-align: center;
    color: #676767;
    margin-top: 150px;
    span {
      font-size: 48px;
    }
    p {
      font-size: 16px;
    }
  }

  .border-count {
    border: 1px solid #0000001f;
    border-radius: 12px;
    font-size: 12px;
    height: 24px;
    text-wrap: nowrap;
    padding: 2px 12px;
  }

  .status {
    background-color: var(--v-success-base);
    color: var(--v-accent-lighten5);
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 4px;
    text-transform: capitalize;
  }
}
.feed-table-image {
  width: 100%;
  overflow: hidden;
  aspect-ratio: 1 / 1;
  border-radius: 4px;
  cursor: pointer;
  background-color: #fff !important;
  &:hover {
    opacity: 0.8;
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
}
::v-deep .v-pagination li button {
  box-shadow: none;
  border: solid 1px #dadce0;
  border-radius: 8px;
}
::v-deep .v-input .v-text-field__suffix {
  font-weight: 400 !important;
}
::v-deep tbody tr {
  cursor: pointer;
}
</style>
